import { formatCurrency, formatPercent, formatNumber } from '../lib/formatters';

interface DemographicCell {
  age_range: string;
  gender: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  ctr: number;
  cpc: number;
  performance_score?: number;
}

interface Props {
  data: DemographicCell[];
  onCellClick: (cell: DemographicCell) => void;
  selectedCell: DemographicCell | null;
}

const AGE_GROUPS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const GENDERS = ['male', 'female', 'unknown'];

export function HeatmapGrid({ data, onCellClick, selectedCell }: Props) {
  // Create lookup map for O(1) access
  const cellMap = new Map<string, DemographicCell>();
  data.forEach(cell => {
    cellMap.set(`${cell.age_range}-${cell.gender}`, cell);
  });

  // Calculate average CTR for comparison
  const ctrs = data.map(d => d.ctr).filter(c => c > 0);
  const avgCtr = ctrs.length > 0 ? ctrs.reduce((a, b) => a + b, 0) / ctrs.length : 0;
  
  // Calculate total spend for percentages
  const totalSpend = data.reduce((sum, d) => sum + d.spend, 0);

  // Get cell color based on CTR performance
  const getCellColor = (ctr: number): string => {
    const ctrValue = ctr / 100; // Convert percentage to decimal
    if (ctrValue >= 0.02) return 'bg-green-500 hover:bg-green-600';
    if (ctrValue >= 0.015) return 'bg-green-400 hover:bg-green-500';
    if (ctrValue >= 0.01) return 'bg-yellow-400 hover:bg-yellow-500';
    if (ctrValue >= 0.005) return 'bg-orange-400 hover:bg-orange-500';
    return 'bg-red-500 hover:bg-red-600';
  };

  // Calculate opacity intensity based on CTR
  const getOpacity = (ctr: number): number => {
    const ctrValue = ctr / 100;
    const normalized = Math.min(ctrValue / 0.03, 1);
    return 0.5 + (normalized * 0.5); // Range: 0.5 to 1.0
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="p-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
              Age / Gender
            </th>
            {GENDERS.map(gender => (
              <th
                key={gender}
                className="p-3 text-center font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 capitalize"
              >
                {gender === 'unknown' ? 'Unknown' : gender}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {AGE_GROUPS.map(age => (
            <tr key={age} className="border-t border-gray-200 dark:border-gray-700">
              <td className="p-3 font-medium text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800">
                {age}
              </td>
              {GENDERS.map(gender => {
                const key = `${age}-${gender}`;
                const cell = cellMap.get(key);
                const isSelected = selectedCell?.age_range === age && 
                                   selectedCell?.gender === gender;

                if (!cell) {
                  return (
                    <td key={key} className="p-2">
                      <div className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 text-sm">
                        No data
                      </div>
                    </td>
                  );
                }

                const spendPct = totalSpend > 0 ? (cell.spend / totalSpend) * 100 : 0;
                const vsPct = avgCtr > 0 ? ((cell.ctr - avgCtr) / avgCtr) * 100 : 0;

                return (
                  <td key={key} className="p-2">
                    <div className="relative group">
                      <button
                        onClick={() => onCellClick(cell)}
                        className={`
                          w-full h-20 rounded-lg transition-all cursor-pointer
                          flex flex-col items-center justify-center
                          ${getCellColor(cell.ctr)}
                          ${isSelected ? 'ring-4 ring-blue-500 ring-offset-2' : ''}
                          shadow-sm hover:shadow-md
                        `}
                        style={{ opacity: getOpacity(cell.ctr) }}
                      >
                        <div className="text-white font-bold text-lg">
                          {formatPercent(cell.ctr / 100)}
                        </div>
                        <div className="text-white/90 text-xs mt-1">
                          {formatCurrency(cell.spend)}
                        </div>
                      </button>

                      {/* Tooltip */}
                      <div className="absolute z-50 invisible group-hover:visible bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 pointer-events-none">
                        <div className="bg-gray-900 text-white p-3 rounded-lg shadow-xl text-sm">
                          <div className="font-bold mb-2 capitalize">
                            {cell.age_range} • {cell.gender}
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                            <span className="text-gray-300">CTR:</span>
                            <span className="font-medium">{formatPercent(cell.ctr / 100)}</span>
                            
                            <span className="text-gray-300">vs Avg:</span>
                            <span className={vsPct >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {vsPct >= 0 ? '+' : ''}{vsPct.toFixed(0)}%
                            </span>
                            
                            <span className="text-gray-300">Impressions:</span>
                            <span>{formatNumber(cell.impressions)}</span>
                            
                            <span className="text-gray-300">Clicks:</span>
                            <span>{formatNumber(cell.clicks)}</span>
                            
                            <span className="text-gray-300">Spend:</span>
                            <span>{formatCurrency(cell.spend)}</span>
                            
                            <span className="text-gray-300">% of Spend:</span>
                            <span>{spendPct.toFixed(1)}%</span>
                            
                            <span className="text-gray-300">Conversions:</span>
                            <span>{formatNumber(cell.conversions)}</span>
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-700 text-xs text-gray-400 text-center">
                            Click for details
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Color Legend Component
export function ColorLegend() {
  return (
    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
      <span className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-red-500" />
        Low (&lt;0.5%)
      </span>
      <span className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-yellow-400" />
        Medium (1-1.5%)
      </span>
      <span className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-green-500" />
        High (&gt;2%)
      </span>
    </div>
  );
}
